import React from 'react';
import { Tooltip, IconButton } from '@mui/material';
import { GavelOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../config/config.json';

interface MakeOfferActionProps {
  item: any;
  sessionToken?: string;
  size?: 'small' | 'medium' | 'large';
  sx?: any;
}

export default function MakeOfferAction({
  item,
  sessionToken,
  size = 'small',
  sx = {},
}: MakeOfferActionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const handleMakeOffer = async () => {
    if (!sessionToken) {
      toast.error(t('auth.mustLoginToOffer'));
      return;
    }

    const amount = window.prompt(t('search.offers.enterAmount'));
    if (!amount) {
      return;
    }

    const numericAmount = parseInt(amount, 10);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      toast.error(t('search.errors.invalidOffer'));
      return;
    }

    try {
      await axios.post(
        config.backend_base_url + 'pss/offer',
        {
          item_id: parseInt(item.id, 10),
          offer: numericAmount.toString(),
        },
        { headers: { 'session-token': sessionToken } }
      );

      toast.success(t('search.item.offerCreated'));
      queryClient.invalidateQueries({ queryKey: ['trackedItems'] });
      queryClient.invalidateQueries({ queryKey: ['trackedProducts'] });
      queryClient.refetchQueries({ queryKey: ['trackedItems'] });
      queryClient.refetchQueries({ queryKey: ['trackedProducts'] });
    } catch (error) {
      toast.error(t('search.errors.createOfferFailed', 'Failed to create offer. Please try again'));
    }
  };

  return (
    <Tooltip title={t('search.offers.makeOfferAmount')}>
      <IconButton
        onClick={handleMakeOffer}
        size={size}
        sx={{
          color: (theme) => theme.palette.text.secondary,
          border: (theme) => `1px solid ${theme.palette.secondary.main}33`,
          '&:hover': {
            color: (theme) => theme.palette.text.primary,
            border: (theme) => `1px solid ${theme.palette.secondary.main}`,
          },
          ...sx,
        }}
      >
        <GavelOutlined fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}
